import { useId } from "react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { BookingMode } from "../types";

interface BookingModeSelectorProps {
  value: BookingMode;
  onChange: (mode: BookingMode) => void;
  disabled?: boolean;
}

interface ModeSwitchRowProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
}

function ModeSwitchRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: ModeSwitchRowProps) {
  const id = useId();
  const descriptionId = `${id}-description`;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0 space-y-1">
        <Label htmlFor={id} className="text-base font-medium">
          {label}
        </Label>
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      </div>
      <Switch
        id={id}
        aria-describedby={descriptionId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

export function BookingModeSelector({
  value,
  onChange,
  disabled = false,
}: BookingModeSelectorProps) {
  const { t } = useTranslation("settings");

  return (
    <SettingCard
      title={t("bookingModeSelector.title")}
      description={t("bookingModeSelector.description")}
      helpDetails={
        <ul className="space-y-2">
          <li>
            <span className="font-medium text-foreground">
              {t("bookingModeSelector.help.off.title")}:
            </span>{" "}
            {t("bookingModeSelector.help.off.description")}
          </li>
          <li>
            <span className="font-medium text-foreground">
              {t("bookingModeSelector.help.on.title")}:
            </span>{" "}
            {t("bookingModeSelector.help.on.description")}
          </li>
        </ul>
      }
    >
      <ModeSwitchRow
        label={t("bookingModeSelector.autoAccept.label")}
        description={t("bookingModeSelector.autoAccept.description")}
        checked={value !== "manual"}
        onCheckedChange={(checked) => onChange(checked ? "auto" : "manual")}
        disabled={disabled}
      />
    </SettingCard>
  );
}
